
import { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Radio,
    RadioGroup,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { InteractionType } from "@azure/msal-browser";
import { useMsalAuthentication } from "@azure/msal-react";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { forecastServerLoginRequest } from "../util/AuthConfig";
import tableStyles from "../styles/table-styles.module.css";

// ── Types ─────────────────────────────────────────────────────────────────────

type FreezeType = "Weekly" | "Monthly";

interface IFreezeSchedule {
    freezE_DAY_ID: number;   // ID from WD_FREEZE_SCHEDULER_DAYS — used for single-day delete
    freezE_ID: number;       // parent schedule ID — used for full schedule delete
    freezE_NAME: string;
    freezE_TYPE: string;
    starT_DATETIME: string;
    enD_DATETIME: string;
    status: string;
    freezE_REASON?: string | null;
    dayS_OF_WEEK?: string | null;
    createD_DATE?: string;
}

interface FreezeSchedulerDto {
    freezeId: number | null;
    freezeName: string;
    freezeType: string;
    startDatetime: string;
    endDatetime: string;
    status: string;
    freezeReason: string;
    daysOfWeek: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const HEADERS_FREEZE_SCHEDULER = ["Freeze Name", "Freeze Type", "Scheduled Start Date", "Scheduled End Date", "Schedule Day", "Actions"];
const HEADERS_UPCOMING = ["Freeze Name", "Freeze Type", "Scheduled Date", "Day", "Status", "Actions"];
const HEADERS_COMPLETED = ["Freeze Name", "Freeze Type", "Scheduled Date", "Day", "Status"];

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso: string | undefined | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}-${dd}-${d.getFullYear()} ${hh}:${min} UTC`;
};

// Shows only the date portion e.g. "08-15-2026" — full datetime visible on hover via Tooltip
const formatDateOnly = (iso: string | undefined | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}-${dd}-${d.getFullYear()}`;
};

// Parses a stored JSON array string like '["Monday","Tuesday"]' into "Monday, Tuesday"
// If the value looks like a date/datetime, returns "—" (bad data guard)
const formatDaysOfWeek = (raw: string | null | undefined): string => {
    if (!raw) return "—";
    // Guard: if it looks like a date (contains digits and dashes in date pattern), don't show it
    if (/^\d{2,4}[-/]\d{2}/.test(raw.trim())) return "—";
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed.join(", ");
    } catch {
        // not JSON — return as-is if it's already a plain string
        if (raw.trim()) return raw;
    }
    return "—";
};

// Produces "2026-08-19T00:00:00" — no UTC offset so the backend parses it as local midnight
// End datetime: use 23:59:59 so that when start === end, start < end is still satisfied at the DB level

// ── Component ─────────────────────────────────────────────────────────────────

export const FreezeScheduler = () => {
    const { execute } = useFetchWithMsal({ scopes: forecastServerLoginRequest.scopes });
    const { result: msalResult } = useMsalAuthentication(InteractionType.Redirect, { scopes: forecastServerLoginRequest.scopes });
    const isReady = !!msalResult;
    const queryClient = useQueryClient();

    // ── Form state ─────────────────────────────────────────────────────────
    const [freezeName, setFreezeName] = useState("");
    const [freezeType, setFreezeType] = useState<FreezeType>("Weekly");
    const [startDate, setStartDate] = useState("");
    const [startTime, setStartTime] = useState("00:00");
    const [endDate, setEndDate] = useState("");
    const [endTime, setEndTime] = useState("23:59");
    const [selectedDays, setSelectedDays] = useState<string[]>([]);
    const [submitted, setSubmitted] = useState(false);

    // ── Table / tab state ──────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);

    // ── Delete confirmation modal state ────────────────────────────────────
    const [deleteTarget, setDeleteTarget] = useState<IFreezeSchedule | null>(null);

    // ── Fetch all three filter sets in parallel so tab counts are always live
    const makeQueryOptions = (filter: string) => ({
        queryKey: ["freezeSchedulers", filter],
        queryFn: async () => {
            const result = await execute<IFreezeSchedule[]>(
                "GET",
                `/api/Freeze/GetFreezeSchedulersByFilters?filterType=${filter}`
            );
            return result ?? [];
        },
        enabled: isReady,
        refetchOnWindowFocus: false,
    });

    const { data: allData = [], isPending, isFetching: f0 } = useQuery<IFreezeSchedule[]>({
        queryKey: ["freezeSchedulers", "ALL_PARENT"],
        queryFn: async () => {
            const result = await execute<IFreezeSchedule[]>("GET", "/api/Freeze/GetAllFreezeSchedulers");
            return result ?? [];
        },
        enabled: isReady,
        refetchOnWindowFocus: false,
    });
    const { data: upcomingData = [], isFetching: f1 } = useQuery<IFreezeSchedule[]>(makeQueryOptions("UPCOMING"));
    const { data: completedData = [], isFetching: f2 } = useQuery<IFreezeSchedule[]>(makeQueryOptions("COMPLETED"));

    const isFetching = f0 || f1 || f2;
    const tabData = [allData, upcomingData, completedData];
    const schedules = tabData[activeTab];

    // ── Save mutation ──────────────────────────────────────────────────────
    const saveMutation = useMutation({
        mutationFn: async (dto: FreezeSchedulerDto) =>
            execute<{ success: boolean; message: string }>("POST", "/api/Freeze/freeze-save", dto),
        onSuccess: (data) => {
            if (data?.success === false) {
                toast.error(data.message || "A freeze schedule already exists for the selected date range.", {
                    autoClose: 8000,
                });
                return;
            }
            toast.success("Freeze schedule saved successfully.");
            queryClient.invalidateQueries({ queryKey: ["freezeSchedulers"] });
            setFreezeName("");
            setStartDate("");
            setStartTime("00:00");
            setEndDate("");
            setEndTime("23:59");
            setSelectedDays([]);
            setSubmitted(false);
        },
        onError: () => toast.error("Failed to save freeze schedule."),
    });

    // ── Delete mutation — single day (tabs 1 & 2) ────────────────────────
    const deleteDayMutation = useMutation({
        mutationFn: async (freezeDayId: number) =>
            execute("DELETE", `/api/Freeze/DeleteFreezeSchedulerDays/${freezeDayId}`),
        onSuccess: () => {
            toast.success("Schedule day deleted.");
            queryClient.invalidateQueries({ queryKey: ["freezeSchedulers"] });
            setDeleteTarget(null);
        },
        onError: () => toast.error("Failed to delete schedule day."),
    });

    // ── Delete mutation — entire schedule (tab 0) ─────────────────────────
    const deleteScheduleMutation = useMutation({
        mutationFn: async (freezeId: number) =>
            execute("DELETE", `/api/Freeze/DeleteFreezeScheduler/${freezeId}`),
        onSuccess: () => {
            toast.success("Freeze schedule deleted.");
            queryClient.invalidateQueries({ queryKey: ["freezeSchedulers"] });
            setDeleteTarget(null);
        },
        onError: () => toast.error("Failed to delete freeze schedule."),
    });

    const handleDeleteClick = (row: IFreezeSchedule) => setDeleteTarget(row);
    const handleDeleteCancel = () => setDeleteTarget(null);

    // A schedule is "currently active" if today falls within its start-end date range
    const isCurrentlyActive = (row: IFreezeSchedule): boolean => {
        if (!row.starT_DATETIME || !row.enD_DATETIME) return false;
        const now = new Date();
        const start = new Date(row.starT_DATETIME);
        const end = new Date(row.enD_DATETIME);
        return now >= start && now <= end;
    };

    const handleDeleteConfirm = () => {
        if (!deleteTarget) return;

        // Block deletion of currently running schedules
        if (isCurrentlyActive(deleteTarget)) {
            toast.error("Active freeze is currently in progress and cannot be deleted.", { autoClose: 8000 });
            setDeleteTarget(null);
            return;
        }

        // Tab 0 (Freeze Scheduler) — grouped by schedule, delete whole schedule
        // Tabs 1 & 2 (Upcoming / Completed) — per-day rows, delete only that day
        if (activeTab === 0) {
            deleteScheduleMutation.mutate(deleteTarget.freezE_ID);
        } else {
            deleteDayMutation.mutate(deleteTarget.freezE_DAY_ID);
        }
    };

    const isDeletePending = deleteDayMutation.isPending || deleteScheduleMutation.isPending;

    // ── Helpers ────────────────────────────────────────────────────────────
    const toggleDay = (day: string) =>
        setSelectedDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
        );

    const maxDaysReached = selectedDays.length >= 6;
    const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
    const dateInvalid = !!startDate && !!endDate && startDate > endDate;
    const startInPast = !!startDate && startDate < today;

    // Monthly freeze: end date cannot exceed start date + 5 days
    const maxEndDate = (() => {
        if (freezeType !== "Monthly" || !startDate) return "";
        const d = new Date(startDate);
        d.setDate(d.getDate() + 5);
        return d.toISOString().split("T")[0];
    })();
    const monthlyEndExceedsLimit = freezeType === "Monthly" && !!startDate && !!endDate && endDate > maxEndDate;

    // ── Save handler ───────────────────────────────────────────────────────
    const handleSave = () => {
        setSubmitted(true);
        if (!freezeName || !startDate || !endDate) return;
        if (freezeType === "Weekly" && selectedDays.length === 0) return;
        if (dateInvalid) return;
        if (startInPast) return;
        if (monthlyEndExceedsLimit) return;

        // Duplicate freeze name check against existing schedules
        const nameExists = allData.some(
            (row) => row.freezE_NAME.trim().toLowerCase() === freezeName.trim().toLowerCase()
        );
        if (nameExists) {
            toast.error(`A freeze schedule with the name "${freezeName}" already exists. Please use a different name.`, { autoClose: 8000 });
            return;
        }

        saveMutation.mutate({
            freezeId: null,
            freezeName,
            freezeType: freezeType.toUpperCase(),
            startDatetime: `${startDate}T${startTime}:00`,
            endDatetime: `${endDate}T${endTime}:59`,
            status: "ACTIVE",
            freezeReason: "",
            // SP expects a JSON array e.g. ["Monday","Tuesday"] for WEEKLY, empty array for MONTHLY
            daysOfWeek: freezeType === "Weekly" ? JSON.stringify(selectedDays) : "[]",
        });
    };

    // ── Filter / paginate ──────────────────────────────────────────────────
    const filtered = schedules.filter(row => {
        const term = searchText.toLowerCase();
        const startFormatted = formatDate(row.starT_DATETIME).toLowerCase();
        const endFormatted = formatDate(row.enD_DATETIME).toLowerCase();
        return (
            (row.freezE_NAME ?? "").toLowerCase().includes(term) ||
            (row.freezE_TYPE ?? "").toLowerCase().includes(term) ||
            formatDaysOfWeek(row.dayS_OF_WEEK).toLowerCase().includes(term) ||
            startFormatted.includes(term) ||
            endFormatted.includes(term)
        );
    });
    const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <Box sx={{ padding: 3 }}>

            {/* Page Title */}
            <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 2 }}>
                Forecast Portal - Freeze Scheduler
            </Typography>

            {/* Create / Add Freeze Schedule Panel */}
            <Paper variant="outlined" sx={{ padding: 3, marginBottom: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#1976d2", marginBottom: 2 }}>
                    Create / Add Freeze Schedule
                </Typography>

                {/* Row 1: Freeze Name | Freeze Type | Start Date | End Date */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "2fr auto 2fr 2fr" }, gap: 2, alignItems: "flex-start", marginBottom: 2 }}>

                    {/* Freeze Name */}
                    <Box>
                        <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                            Freeze Name <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <TextField
                                size="small"
                                fullWidth
                                value={freezeName}
                                onChange={(e) => setFreezeName(e.target.value)}
                                placeholder="e.g. Weekly Forecast Freeze"
                                error={submitted && !freezeName}
                            />
                            {submitted && !freezeName && (
                                <Typography variant="caption" color="error">Freeze Name is required.</Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Freeze Type */}
                    <Box>
                        <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                            Freeze Type <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <RadioGroup
                            row
                            value={freezeType}
                            onChange={(e) => {
                                setFreezeType(e.target.value as FreezeType);
                                setSelectedDays([]);
                            }}
                        >
                            <FormControlLabel value="Weekly" control={<Radio size="small" />} label="Weekly" />
                            <FormControlLabel value="Monthly" control={<Radio size="small" />} label="Monthly" />
                        </RadioGroup>
                    </Box>

                    {/* Effective Start Date */}
                    <Box>
                        <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                            Effective Start Date <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    sx={{ flex: 1 }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: today }}
                                    error={submitted && (!startDate || dateInvalid || startInPast)}
                                />
                            </Box>
                            {submitted && !startDate && (
                                <Typography variant="caption" color="error">Start Date is required.</Typography>
                            )}
                            {submitted && startDate && startInPast && (
                                <Typography variant="caption" color="error">Start Date cannot be in the past.</Typography>
                            )}
                            {submitted && startDate && !startInPast && dateInvalid && (
                                <Typography variant="caption" color="error">Start Date must be before End Date.</Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Effective End Date */}
                    <Box>
                        <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                            Effective End Date <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    sx={{ flex: 1 }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: startDate, max: maxEndDate || undefined }}
                                    error={submitted && (!endDate || monthlyEndExceedsLimit)}
                                />
                            </Box>
                            {submitted && !endDate && (
                                <Typography variant="caption" color="error">End Date is required.</Typography>
                            )}
                            {submitted && monthlyEndExceedsLimit && (
                                <Typography variant="caption" color="error">
                                    Monthly freeze end date cannot exceed 5 days from start date (max: {formatDateOnly(maxEndDate)}).
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Row 2: Day selector + Save button */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                    {freezeType === "Weekly" && (
                        <Box>
                            <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                                Weekly Schedule Days <span style={{ color: "red" }}>*</span>
                                {maxDaysReached && (
                                    <span style={{ color: "red", marginLeft: 8, fontSize: 12 }}>
                                        (Maximum 6 days can be selected)
                                    </span>
                                )}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0, flexWrap: "wrap" }}>
                                {DAYS_OF_WEEK.map((day) => {
                                    const isDisabled = maxDaysReached && !selectedDays.includes(day);
                                    return (
                                        <FormControlLabel
                                            key={day}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedDays.includes(day)}
                                                    onChange={() => toggleDay(day)}
                                                    disabled={isDisabled}
                                                />
                                            }
                                            label={day}
                                            sx={{ marginRight: 1, opacity: isDisabled ? 0.4 : 1 }}
                                        />
                                    );
                                })}
                            </Box>
                            {submitted && freezeType === "Weekly" && selectedDays.length === 0 && (
                                <Typography variant="caption" color="error">Please select at least one day.</Typography>
                            )}

                            {maxDaysReached && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        backgroundColor: "#fff8e1",
                                        border: "1px solid #ffe082",
                                        borderRadius: 1,
                                        padding: "10px 14px",
                                        marginTop: 1,
                                        maxWidth: 600,
                                    }}
                                >
                                    <span style={{ color: "#f59e0b", fontSize: 18, lineHeight: 1.2 }}>⚠</span>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "#b45309" }}>
                                            Maximum freeze limit reached.
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "#78350f" }}>
                                            All 7 days cannot be selected for Freeze Scheduling. Please leave at least one day available for normal operations.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        className={tableStyles.buttonColor}
                        startIcon={saveMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saveMutation.isPending}
                        sx={{ height: 40, minWidth: 150, textTransform: "none", marginLeft: "auto" }}
                    >
                        {saveMutation.isPending ? "Saving…" : "Save Schedule"}
                    </Button>
                </Box>
            </Paper>

            {/* Tabs — counts are live from each filter's query */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => { setActiveTab(v); setPage(0); setSearchText(""); }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label={`Freeze Scheduler (${allData.length})`} />
                    <Tab label={`Upcoming Scheduled Dates (${upcomingData.length})`} />
                    <Tab label={`Completed Schedules (${completedData.length})`} />
                </Tabs>
            </Box>

            {/* Search */}
            <Box sx={{ marginBottom: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search by Freeze Name, Freeze Type, Day or Date"
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                    sx={{ width: 320 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Loading */}
            {(isPending || isFetching) && (
                <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {/* Table */}
            {!isPending && (
                <TableContainer sx={{ boxShadow: 3 }}>
                    <Table
                        id="freeze-scheduler-table"
                        sx={{ minWidth: 700, borderCollapse: "collapse" }}
                        size="small"
                    >
                        <TableHead>
                            <TableRow>
                                {(activeTab === 0
                                    ? HEADERS_FREEZE_SCHEDULER
                                    : activeTab === 1
                                        ? HEADERS_UPCOMING
                                        : HEADERS_COMPLETED
                                ).map((header) => (
                                    <TableCell
                                        key={header}
                                        align={header === "Actions" ? "center" : "left"}
                                        sx={{
                                            border: "1px solid #ddd",
                                            backgroundColor: "#f5f5f5",
                                            color: "#333",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.length > 0 ? (
                                paginated.map((row, idx) => (
                                    <TableRow
                                        key={activeTab === 0 ? row.freezE_ID : `${row.freezE_ID}-${idx}`}
                                        sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                                    >
                                        <TableCell sx={{ border: "1px solid #ddd" }}>{row.freezE_NAME}</TableCell>
                                        <TableCell sx={{ border: "1px solid #ddd" }}>{row.freezE_TYPE}</TableCell>

                                        {activeTab === 0 ? (
                                            <>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>{formatDate(row.starT_DATETIME)}</TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>{formatDate(row.enD_DATETIME)}</TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>{formatDaysOfWeek(row.dayS_OF_WEEK)}</TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }} align="center">
                                                    <Tooltip title={isCurrentlyActive(row) ? "Active freeze cannot be deleted" : ""} arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                aria-label="delete"
                                                                disabled={isDeletePending || isCurrentlyActive(row)}
                                                                onClick={() => handleDeleteClick(row)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </>
                                        ) : activeTab === 1 ? (
                                            <>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>
                                                    <Tooltip title={formatDate(row.starT_DATETIME)} arrow placement="top">
                                                        <span>{formatDateOnly(row.starT_DATETIME)}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>{formatDaysOfWeek(row.dayS_OF_WEEK)}</TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>
                                                    <span style={{ backgroundColor: "#d4edda", color: "#155724", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: "bold" }}>
                                                        {row.status}
                                                    </span>
                                                </TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }} align="center">
                                                    <Tooltip title={isCurrentlyActive(row) ? "Active freeze cannot be deleted" : ""} arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                aria-label="delete"
                                                                disabled={isDeletePending || isCurrentlyActive(row)}
                                                                onClick={() => handleDeleteClick(row)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>
                                                    <Tooltip title={formatDate(row.starT_DATETIME)} arrow placement="top">
                                                        <span>{formatDateOnly(row.starT_DATETIME)}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>{formatDaysOfWeek(row.dayS_OF_WEEK)}</TableCell>
                                                <TableCell sx={{ border: "1px solid #ddd" }}>
                                                    <span style={{ backgroundColor: "#e2e3e5", color: "#383d41", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: "bold" }}>
                                                        {row.status}
                                                    </span>
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ padding: 3, color: "#888" }}>
                                        {isFetching ? "Loading…" : "No schedules found."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            {filtered.length > rowsPerPage && (
                <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={() => { }}
                    rowsPerPageOptions={[]}
                    labelDisplayedRows={({ from, to, count }) =>
                        count === 0 ? "No records" : `${from}-${to} of ${count}`
                    }
                />
            )}

            {/* Delete Confirmation Modal */}
            <Dialog
                open={!!deleteTarget}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <WarningAmberIcon sx={{ color: "#f59e0b" }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Delete Freeze Schedule
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleDeleteCancel} aria-label="close">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Are you sure you want to delete this schedule?
                    </Typography>

                    {/* Schedule details */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 0.75, columnGap: 1, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Freeze Name</Typography>
                        <Typography variant="body2">: &nbsp;{deleteTarget?.freezE_NAME}</Typography>

                        <Typography variant="body2" color="text.secondary">Freeze Type</Typography>
                        <Typography variant="body2">
                            : &nbsp;{deleteTarget ? deleteTarget.freezE_TYPE.charAt(0).toUpperCase() + deleteTarget.freezE_TYPE.slice(1).toLowerCase() : ""}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">Schedule Day</Typography>
                        <Typography variant="body2">: &nbsp;{formatDaysOfWeek(deleteTarget?.dayS_OF_WEEK)}</Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Impact summary */}
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#1976d2", mb: 0.5 }}>
                        Impact Summary
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        All occurrences related to this schedule will be deleted from the Upcoming schedule
                    </Typography>

                    {/* Cannot be undone warning */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#d32f2f", mb: 2 }}>
                        <WarningAmberIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            This action cannot be undone.
                        </Typography>
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={handleDeleteCancel}
                            disabled={isDeletePending}
                            sx={{ textTransform: "none", minWidth: 90 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={isDeletePending ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                            onClick={handleDeleteConfirm}
                            disabled={isDeletePending}
                            sx={{ textTransform: "none", minWidth: 90 }}
                        >
                            {isDeletePending ? "Deleting…" : "Delete"}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
